import { useCallback, useEffect, useRef, useState } from 'react';

export const SLIDE_OVER_DURATION_MS = 300;

/**
 * Drives a right-edge slide-over's enter/exit animation.
 *
 * The panel animates in on mount, and `close()` plays the exit animation before
 * invoking `onClose` (which typically unmounts the panel). Deferring the unmount
 * is what lets the slide-out actually be seen. Also wires Escape-to-close.
 */
export function useSlideOver(onClose: () => void, durationMs = SLIDE_OVER_DURATION_MS) {
  const [entered, setEntered] = useState(false);
  const [closing, setClosing] = useState(false);
  const closedRef = useRef(false);

  useEffect(() => {
    const raf = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  const close = useCallback(() => {
    if (closedRef.current) return;
    closedRef.current = true;
    setClosing(true);
    window.setTimeout(onClose, durationMs);
  }, [onClose, durationMs]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [close]);

  // `open` = should currently sit in the visible (on-screen) position.
  return { open: entered && !closing, close };
}
